import { useParams } from 'react-router-dom';
import { useMemo, useRef, useState } from 'react';

import { AxiosResponse } from 'axios';
import { AgGridReact } from 'ag-grid-react';
import { isEmpty, isFunction } from 'lodash-es';
import { ColDef, GetQuickFilterTextParams, GetRowIdParams, GridReadyEvent, ICellRendererParams, IRowNode, ModuleRegistry, SelectionChangedEvent, ValueFormatterParams } from 'ag-grid-community';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import type { UseMutateFunction } from '@tanstack/react-query';

import { Box, Button, dayjs, Menu, SearchIcon, Snackbar, TextField, AgGridTable } from '@parspec/pixel';

import { useDownloadDocument } from 'src/features/Project/queries';
import CustomNoRowsOverlay from '../OverLayProjectTable';
import { useSnackbar } from 'src/features/shared/hooks/useSnackbar';
import RenameModal from 'src/features/Project/RenameProjectModal';
import { callProxyURL } from '../../BOM/FinalDocuments/utils/downloadFile';
import { getDateStringForTable } from 'src/features/shared/utils/utils';
import { DOWNLOAD_FAILED_MSG } from 'src/features/shared/constants';
import { SnackbarMessages } from '../../BOM/FinalDocuments/constants';
import { DeleteConfirmationModal, FieldNameRenderer, EmptyFolderRenderer } from './component';
import { defaultColDef, formatFileSize } from './utils';
import { headerIconComponent } from '../HeaderIconTable';
import { NestedFolderResponse } from 'src/features/Project/queries/apiTypes';
import { CustomRowItem, ProjectFileRow } from './types';
import MoveFileModal from './MoveFileModal';
import ProjectFilesUploadModal from 'src/features/Project/UploadProjectFilesModal';
import AddFolderModal from './AddFolderModal';
import { stringComparator } from 'src/features/Dashboard/shared/utils';
import { ProjectsTableContext } from './context';

ModuleRegistry.registerModules([ClientSideRowModelModule, RowGroupingModule]);

interface ProjectsTableProps {
    isLoading?: boolean;
    nestedFolders: NestedFolderResponse[];
    hideContextMenu?: boolean;
    // we only allow selection of file rows, not folders
    onSelectionChanged?: (params: SelectionChangedEvent<ProjectFileRow>) => void;
    tableHeight?: number | string;
    isActionButtonsVisible?: boolean;
    noFilesText?: string;
}

interface DeleteData {
    entityId: number; // entityId => file or folder id
    projectId: number;
    isFolder: boolean;
}

interface RenameData {
    id: number;
    name: string;
    isFolder: boolean;
}

interface MoveFileData {
    projectId: number;
    documentId: number;
    prevFolderId: number;
}

type ModalState =
    | { type: 'DELETE'; data: DeleteData }
    | { type: 'RENAME'; data: RenameData }
    | { type: 'MOVE_FILE'; data: MoveFileData }
    | { type: 'UPLOAD_FILE' }
    | { type: 'CREATE_FOLDER' }
    | { type: null }; // To represent that the modal is closed

interface MenuOptionsProps<TData> extends ICellRendererParams<TData> {
    foldersSize: number;
    projectId: number;
    setSelectedFileForAction: React.Dispatch<React.SetStateAction<ModalState>>;
    downloadDocument: UseMutateFunction<AxiosResponse, unknown, {
        projectId: number;
        documentId: number;
    }, unknown>
    setSnackbarOpen: (message: string, autoHideDuration?: number | null) => void;
}

const MenuOptions = (props: MenuOptionsProps<CustomRowItem>) => {
    const {
        data,
        node,
        projectId,
        setSelectedFileForAction,
        downloadDocument,
        setSnackbarOpen,
        foldersSize,
    } = props;

    const getOptions = () => {
        if (!data) return [];
        const { id, isFolder } = data;

        const isDefaultFolder = isFolder && data.is_default;
        const isNonEmptyFolder = isFolder && (node.allChildrenCount ?? 0) > 0;

        const downloadOption = {
            id: 'download',
            label: 'Download',
            onClick: () => {
                if (isFolder) return;

                const { file_type, file_name } = data;
                downloadDocument(
                    { projectId, documentId: id },
                    {
                        onSuccess: async (data: AxiosResponse) => {
                            await callProxyURL(data?.data.s3_url, file_type, file_name);
                            setSnackbarOpen(SnackbarMessages.DOWNLOAD_SUCCESS);
                        },
                        onError: () => {
                            setSnackbarOpen(DOWNLOAD_FAILED_MSG);
                        }
                    }
                );
            }
        }

        const moveFileOption = {
            id: 'move',
            label: 'Move',
            // meaning no more folders to move to, and no tooltip needed as per product team
            disabled: foldersSize === 1,
            onClick: () => {
                if (isFolder || foldersSize === 1) return;
                const prevFolderId = node.parent?.data?.id;
                if (!prevFolderId) return;

                setSelectedFileForAction({
                    type: "MOVE_FILE",
                    data: { documentId: id, prevFolderId, projectId },
                });
            },
            addDivider: !isFolder,
            dividerColor: 'neutral.light'
        }

        const options = [
            {
                id: 'rename',
                label: 'Rename',
                onClick: () => {
                    setSelectedFileForAction({
                        type: "RENAME",
                        data: { id, name: isFolder ? data.name : data.file_name, isFolder }
                    });
                },
                addDivider: Boolean(isFolder),
                dividerColor: 'neutral.light'
            },
            ...(isFolder ? [] : [downloadOption, moveFileOption]),
            {
                id: 'delete',
                label: 'Delete',
                disabled: isDefaultFolder || isNonEmptyFolder,
                toolTipMessage:
                    isDefaultFolder
                        ? "This folder cannot be deleted, as it is a default folder."
                        : isNonEmptyFolder
                            ? "Please remove all files from folder in order to delete it"
                            : undefined,
                onClick: async () => {
                    setSelectedFileForAction({ type: "DELETE", data: { projectId, entityId: id, isFolder } });
                },
                color: 'error.main',
                hoverBackgroundColor: 'error.light'
            }
        ];
        return options;
    };

    return (
        <Box display="flex" justifyContent="center">
            <Menu options={getOptions()} />
        </Box>
    );
};

const getFileSize = (
    props: ValueFormatterParams<CustomRowItem> | GetQuickFilterTextParams<CustomRowItem>
) => {
    if (!props.data || props.data.isFolder) return "";
    return formatFileSize(props.data.file_size);
};

const getUniqueFolderId = (id: number) => `folder-${id}`;

const getRowId = (params: GetRowIdParams<CustomRowItem>) =>
    params.data.isFolder ? getUniqueFolderId(params.data.id) : `${params.data.id}`;

const getDataPath = (data: CustomRowItem) => data.path;

const autoGroupColDef: ColDef<CustomRowItem> = {
    colId: 'file_name',
    headerName: 'Name',
    minWidth: 240,
    width: 300,
    // NOTE: Deliberately combining folder name and document count in valueGetter for folder
    // to re-render the cell when document count changes.(Else it won't re-render on document count change)
    valueGetter: (params) => params.data?.isFolder
        ? `${params.data?.name} (${params.data?.document_count})`
        : params.data?.file_name,
    valueFormatter: (params) => {
        if (!params.data) return "";
        return params.data.isFolder ? params.data.name : params.data.file_name;
    },
    getQuickFilterText: (params) => {
        return params.data.isFolder ? params.data.name : params.data.file_name;
    },
    comparator: (_valueA: string, _valueB: string, nodeA, nodeB) => {
        if (!nodeA.data || !nodeB.data) return 0;

        // Getting the raw values from node's data to compare
        const valueA = nodeA.data.isFolder ? nodeA.data.name : nodeA.data.file_name;
        const valueB = nodeB.data.isFolder ? nodeB.data.name : nodeB.data.file_name;
        return stringComparator(valueA, valueB);
    },
    cellRendererParams: {
        innerRendererSelector: (params: ICellRendererParams<CustomRowItem>) => {
            if (params.node.level === 0) {
                // For empty folder(parent) row, return custom inner renderer
                if (params.data?.isFolder && !params.data.document_count) {
                    return { component: EmptyFolderRenderer };
                }

                // If it's non-empty folder(parent) row, return undefined to let Aggrid handle UI itself
                return undefined;
            }
            return {
                component: FieldNameRenderer
            };
        },
    },
    sortable: true,
    suppressMenu: true,
    unSortIcon: true,
};

const isRowSelectable = (params: IRowNode<CustomRowItem>) => !params.data?.isFolder;

const ProjectsFilesTable = ({
    nestedFolders,
    hideContextMenu = false,
    onSelectionChanged,
    isLoading = false,
    tableHeight = 232,
    isActionButtonsVisible = false,
    noFilesText,
}: ProjectsTableProps) => {
    const { projectId } = useParams();

    const { mutate: downloadDocument } = useDownloadDocument();

    const [selectedFileForAction, setSelectedFileForAction] = useState<ModalState>({ type: null });

    const [searchText, setSearchText] = useState('');

    const gridRef = useRef<AgGridReact<CustomRowItem> | null>(null);
    const { snackbarInfo, setSnackbarOpen, setSnackbarClose } = useSnackbar();

    const handleCloseModal = () => {
        setSelectedFileForAction({ type: null });
    };

    const foldersSize = nestedFolders.length;

    const nestedRowData = useMemo(() => {
        if (isEmpty(nestedFolders)) return [];

        // NOTE: group by folders and documents
        const rows = nestedFolders.reduce<CustomRowItem[]>((acc, folder) => {
            // Adding folder prefix to make the path unique(in case some file has same id as folder)
            const folderPath = getUniqueFolderId(folder.id);
            const lowerCaseSearchText = searchText.toLowerCase();

            const folderMatches = folder.name.toLowerCase().includes(lowerCaseSearchText);

            // If folder matches, add it to the rows and all its documents
            if (folderMatches) {
                acc.push({ ...folder, path: [folderPath], isFolder: true });

                folder.documents.forEach((document) => {
                    acc.push({ ...document, path: [folderPath, document.id.toString()], isFolder: false });
                });
            } else {
                // If folder doesn't match, check if it has any documents that match and then add
                let isFolderAdded = false;
                folder.documents.forEach((document) => {
                    const isDocumentMatches = document.file_name.toLowerCase().includes(lowerCaseSearchText);

                    if (isDocumentMatches) {
                        if (!isFolderAdded) {
                            acc.push({ ...folder, path: [folderPath], isFolder: true });
                            isFolderAdded = true;
                        }
                        acc.push({ ...document, path: [folderPath, document.id.toString()], isFolder: false });
                    }
                });
            }

            return acc;
        }, []);

        return rows;
    }, [searchText, nestedFolders]);

    const columnDefs = useMemo<ColDef[]>(() => {
        const columns: ColDef<CustomRowItem>[] = [
            {
                colId: 'created_by_name',
                headerName: 'Uploaded by',
                minWidth: 182,
                sortable: true,
                suppressMenu: true,
                unSortIcon: true,
                comparator: (valueA: string, valueB: string) => stringComparator(valueA, valueB),
                cellClassRules: {
                    "ag-grid-disable-cell": (args) => !!args.data?.isFolder,
                },
                valueGetter: (params) => {
                    const { data } = params;
                    if (!data || data.isFolder) return "";
                    return data.created_by_name;
                },
            },
            {
                colId: 'created_at',
                headerName: 'Uploaded Date',
                minWidth: 182,
                sortable: true,
                suppressMenu: true,
                unSortIcon: true,
                cellClassRules: {
                    "ag-grid-disable-cell": (args) => !!args.data?.isFolder,
                },
                valueGetter: (params) => {
                    const { data } = params;
                    if (!data || data.isFolder) return "";
                    return data.created_at ? getDateStringForTable(data.created_at) : '-';
                },
                comparator: (_valueA, _valueB, nodeA, nodeB) => {
                    // add logic to compare dates using dayjs
                    if (!nodeA.data || !nodeB.data) return 0;
                    if (nodeA.data.isFolder || nodeB.data.isFolder) return 0;

                    const dateA = dayjs(nodeA.data.created_at);
                    const dateB = dayjs(nodeB.data.created_at);
                    if (dateA.isBefore(dateB)) return -1;
                    if (dateA.isAfter(dateB)) return 1;
                    return 0;
                },
            },
            {
                field: 'file_size',
                headerName: 'Size',
                minWidth: 130,
                cellClassRules: {
                    "ag-grid-disable-cell": (args) => !!args.data?.isFolder,
                },
                valueFormatter: getFileSize,
                suppressMenu: true,
                sortable: true,
                unSortIcon: true,
                getQuickFilterText: getFileSize,
            },
            {
                headerName: '',
                headerComponent: headerIconComponent,
                cellRenderer: (props: ICellRendererParams) =>
                    <MenuOptions
                        {...props}
                        projectId={Number(projectId)}
                        setSelectedFileForAction={setSelectedFileForAction}
                        downloadDocument={downloadDocument}
                        setSnackbarOpen={setSnackbarOpen}
                        foldersSize={foldersSize}
                    />,
                minWidth: 50,
                width: 65,
                colId: 'menu',
                suppressMenu: true,
                lockPosition: 'right',
                lockPinned: true,
                pinned: 'right',
                hide: hideContextMenu
            }
        ];
        return columns;
    }, [
        hideContextMenu,
        projectId,
        setSelectedFileForAction,
        downloadDocument,
        setSnackbarOpen,
        foldersSize,
    ]);

    const noRowsOverlayComponentParams = useMemo(() => ({
        noRowsMessageFunc: () => noFilesText || 'No files to show',
    }), [noFilesText]);

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();
    };


    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.target.value);
    };

    const openCreateFolderModal = () => {
        setSelectedFileForAction({ type: 'CREATE_FOLDER' });
    };

    const openUploadFileModal = () => {
        setSelectedFileForAction({ type: 'UPLOAD_FILE' });
    }

    const renderModals = () => {
        switch (selectedFileForAction.type) {
            case 'RENAME':
                return (
                    <RenameModal
                        open
                        handleCloseRenameModal={handleCloseModal}
                        uploadedFileDetails={selectedFileForAction.data}
                        handleUploadedFileName={() => setSnackbarOpen(SnackbarMessages.RENAME_SUCCESS)}
                    />
                )
            case 'DELETE':
                return (
                    <DeleteConfirmationModal
                        open
                        data={selectedFileForAction.data}
                        onClose={handleCloseModal}
                        onDeleteSuccess={(message) => setSnackbarOpen(message || SnackbarMessages.DELETE_SUCCESS)}
                    />
                );
            case 'MOVE_FILE':
                return (
                    <MoveFileModal
                        open
                        data={selectedFileForAction.data}
                        onClose={handleCloseModal}
                        onFileMoveSuccess={() => setSnackbarOpen(SnackbarMessages.FILE_MOVED_SUCCESS)}
                        folders={nestedFolders.filter((folder) => folder.id !== selectedFileForAction.data.prevFolderId)}
                    />
                );
            case 'UPLOAD_FILE':
                return (
                    <ProjectFilesUploadModal
                        openModal
                        closeProjectFilesModal={handleCloseModal}
                        folders={nestedFolders}
                    />
                );
            case 'CREATE_FOLDER':
                return (
                    <AddFolderModal
                        open
                        onClose={handleCloseModal}
                        projectId={Number(projectId)}
                        onCreateFolderSuccess={() => setSnackbarOpen(SnackbarMessages.FOLDER_CREATED_SUCCESS)}
                    />
                );
            default:
                return null;
        }
    }

    return (
        <Box width="100%" display="flex" flexDirection="column" gap="8px">
            <Box display="flex" justifyContent="space-between">
                <Box width={320}>
                    <TextField
                        label=""
                        placeholder="Search"
                        startIcon={<SearchIcon fontSize="small" />}
                        size="small"
                        value={searchText}
                        onChange={handleSearch}
                    />
                </Box>
                {isActionButtonsVisible && (
                    <Box display="flex" gap="8px">
                        <Button
                            variant='outlined'
                            color='secondary'
                            sx={{ backgroundColor: 'white' }}
                            onClick={openCreateFolderModal}
                        >
                            Create Folder
                        </Button>
                        <Button onClick={openUploadFileModal}>Upload File(s)</Button>
                    </Box>
                )}
            </Box>
            <ProjectsTableContext.Provider value={{ setSnackbarOpen }}>
                <AgGridTable
                    ref={gridRef}
                    showToolbarPanel={false}
                    isLoading={isLoading}
                    tableHeight="100%"
                    outerContainerSX={{ height: tableHeight }}
                    // pass null instead of empty [] in rowData when isLoading to actually render the loader
                    rowData={isLoading ? null : nestedRowData}
                    getRowId={getRowId}
                    columnDefs={columnDefs}
                    onGridReady={onGridReady}
                    noRowsOverlayComponent={CustomNoRowsOverlay}
                    noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                    rowSelection={isFunction(onSelectionChanged) ? "single" : undefined}
                    isRowSelectable={isRowSelectable}
                    onSelectionChanged={onSelectionChanged}
                    suppressContextMenu
                    suppressRowClickSelection={false}
                    defaultColDef={defaultColDef}
                    quickFilterText={searchText}
                    suppressMenuHide
                    treeData
                    getDataPath={getDataPath}
                    autoGroupColumnDef={autoGroupColDef}
                    groupDefaultExpanded={1}
                />
            </ProjectsTableContext.Provider>

            {renderModals()}

            <Snackbar
                autoHideDuration={snackbarInfo.autoHideDuration ?? 5000}
                open={snackbarInfo.open}
                message={snackbarInfo.message}
                onClose={setSnackbarClose}
            />
        </Box>
    );
};

export default ProjectsFilesTable;
